import { useCallback, useEffect, useSyncExternalStore } from 'react';

// Per-browser, admin-only feature gate for the Plaid-backed transactions
// surface. Defaults OFF for every user and every browser — Apple Card
// purchases aren't available through Plaid, so the partial actuals would
// mislead. The admin (see config/admin.ts) can flip it on locally to dev
// against, and Settings hides the toggle for everyone else.

const STORAGE_KEY = 'budget.transactionsEnabled';

const listeners = new Set<() => void>();

function read(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function write(next: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    if (next) window.localStorage.setItem(STORAGE_KEY, '1');
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  for (const l of listeners) l();
}

function subscribe(callback: () => void): () => void {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

// Synchronous accessor for callers that only need the current value (e.g.
// the auto-sync hook deciding whether to fire — it doesn't need to re-render
// when the flag flips, just read the latest value when it next runs).
export function transactionsEnabled(): boolean {
  return read();
}

export function useTransactionsEnabled(): {
  enabled: boolean;
  setEnabled: (next: boolean) => void;
} {
  const enabled = useSyncExternalStore(subscribe, read, () => false);

  // Cross-tab sync: storage event fires in OTHER tabs when this tab writes.
  // Triggering the local notify path keeps every subscriber on every tab in
  // step without a full reload.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        for (const l of listeners) l();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setEnabled = useCallback((next: boolean) => write(next), []);
  return { enabled, setEnabled };
}
